import { blobToDataURL, dataURLToBlob } from 'blob-util';
import { Dexie } from 'dexie';
import { isChrome, isEdge, isIOS } from 'react-device-detect';
import { TooLowStorageError } from './types';

export const MINIMUM_STORAGE_BYTES = 5 * 1024 * 1024; // 5mb required to start recording

// Local copy so the engine carries no app util dependency.
export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const linearChunkDurations = [
  { minDuration: 10000, maxDuration: 30000 },
  { minDuration: 20000, maxDuration: 40000 },
  { minDuration: 40000, maxDuration: 60000 },
];

export const dictationChunkDurations = [{ minDuration: 45000, maxDuration: 60000 }];

export const serializeBlob = async (
  blob?: Blob | string
): Promise<Blob | string | undefined> => {
  if (!blob) return;
  if (typeof blob === 'string') return blob;
  if (isChrome && !isIOS) return blob;
  if (isEdge && !isIOS) return blob;
  return blobToDataURL(blob);
};

export const deserializeBlob = (blob?: Blob | string) => {
  if (!blob) return;
  if (typeof blob !== 'string') return blob;
  return dataURLToBlob(blob);
};

const getStorageEstimation = async () => {
  if (navigator.storage && navigator.storage.estimate) {
    const estimation = await navigator.storage.estimate();
    return { quota: estimation.quota, usage: estimation.usage };
  } else {
    return { quota: undefined, usage: undefined };
  }
};

export const checkStorageAvailable = async () => {
  const { quota, usage } = await getStorageEstimation();
  if (quota && usage && quota - usage < MINIMUM_STORAGE_BYTES) {
    throw new TooLowStorageError(
      `Storage too low to begin recording, quota: ${quota}, usage: ${usage}`
    );
  }
};

export const getErrorInfo = async (error: unknown) => {
  if (error instanceof Error) {
    const errorInfo: { [key: string]: string | number } = {
      errorName: error.name.trim(),
      errorMessage: error.message.trim(),
    };
    const addStorageInfo =
      error instanceof Dexie.DexieError || error instanceof TooLowStorageError;
    if (addStorageInfo) {
      const { quota, usage } = await getStorageEstimation();
      errorInfo.storageQuota = quota || 'Unable to determine quota';
      errorInfo.storageUsage = usage || 'Unable to determine usage';
    }
    return errorInfo;
  }
};
